/**
 * Cognito Integration Service
 * 
 * Provides integration with AWS Cognito for user authentication,
 * registration, password management, MFA, and social login.
 * 
 * Uses AWS SDK v3 directly instead of Amplify for better control
 * and compatibility with the existing User API Cognito pool.
 */

import {
    AssociateSoftwareTokenCommand,
    ChallengeNameType,
    ChangePasswordCommand,
    CognitoIdentityProviderClient,
    ConfirmForgotPasswordCommand,
    ConfirmSignUpCommand,
    ForgotPasswordCommand,
    GetUserCommand,
    GlobalSignOutCommand,
    InitiateAuthCommand,
    InitiateAuthCommandOutput,
    ResendConfirmationCodeCommand,
    RespondToAuthChallengeCommand,
    RespondToAuthChallengeCommandOutput,
    SetUserMFAPreferenceCommand,
    SignUpCommand,
    UpdateUserAttributesCommand,
    VerifySoftwareTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider';

/**
 * OAuth token response from Cognito hosted UI
 */
interface OAuthTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Session state for tracking current authentication
 */
interface SessionState {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  username?: string;
}


// Get environment variables
const getEnvVar = (key: string, defaultValue = ''): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env[key] as string) || defaultValue;
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  return defaultValue;
};

// Cognito configuration
const COGNITO_REGION = getEnvVar('VITE_COGNITO_REGION', 'us-east-1');
const USER_POOL_ID = getEnvVar('VITE_COGNITO_USER_POOL_ID', 'us-east-1_aZw1LIiFt');
const CLIENT_ID = getEnvVar('VITE_COGNITO_CLIENT_ID', '');

// Create Cognito client
const cognitoClient = new CognitoIdentityProviderClient({
  region: COGNITO_REGION,
});

// Suppress unused variable warning - USER_POOL_ID is used for configuration reference
void USER_POOL_ID;


/**
 * Result of Cognito authentication operations
 */
export interface CognitoAuthResult {
  isSignedIn: boolean;
  userId?: string;
  username?: string;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  nextStep?: {
    signInStep: string;
    challengeName?: string;
    session?: string;
    additionalInfo?: Record<string, unknown>;
  };
}

/**
 * User data for registration
 */
export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
}

/**
 * User attribute updates
 */
export interface UserUpdates {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
}

/**
 * Cognito user information
 */
export interface CognitoUser {
  userId: string;
  username: string;
  email: string;
  emailVerified: boolean;
  attributes: Record<string, string>;
}

/**
 * MFA setup data from Cognito
 */
export interface MFASetupData {
  secretCode: string;
  qrCodeUrl: string;
  session?: string;
}


/**
 * Cognito Integration Service
 * 
 * Handles all interactions with AWS Cognito User Pools using AWS SDK v3
 */
export class CognitoService {
  private currentSession: SessionState = {};

  /**
   * Authenticate a user with email and password
   */
  async authenticateUser(email: string, password: string): Promise<CognitoAuthResult> {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);
      return this.handleAuthResponse(response, email);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Handle authentication response and challenges
   */
  private handleAuthResponse(
    response: InitiateAuthCommandOutput | RespondToAuthChallengeCommandOutput,
    username: string
  ): CognitoAuthResult {
    // Check for challenges (MFA, new password required, etc.)
    if (response.ChallengeName) {
      return {
        isSignedIn: false,
        username,
        nextStep: {
          signInStep: this.mapChallengeName(response.ChallengeName),
          challengeName: response.ChallengeName,
          session: response.Session,
          additionalInfo: response.ChallengeParameters as Record<string, unknown>,
        },
      };
    }

    // Successful authentication
    if (response.AuthenticationResult) {

      return {
        isSignedIn: true,
        username,
        accessToken: response.AuthenticationResult.AccessToken,
        idToken: response.AuthenticationResult.IdToken,
        refreshToken: response.AuthenticationResult.RefreshToken,
      };
    }

    return { isSignedIn: false };
  }

  /**
   * Map Cognito challenge names to user-friendly step names
   */
  private mapChallengeName(challengeName: ChallengeNameType): string {
    const challengeMap: Record<string, string> = {
      SMS_MFA: 'CONFIRM_SIGN_IN_WITH_SMS_CODE',
      SOFTWARE_TOKEN_MFA: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
      NEW_PASSWORD_REQUIRED: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
      MFA_SETUP: 'CONTINUE_SIGN_IN_WITH_MFA_SETUP',
      SELECT_MFA_TYPE: 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION',
    };
    return challengeMap[challengeName] || challengeName;
  }


  /**
   * Respond to MFA challenge
   */
  async respondToMFAChallenge(
    code: string,
    session: string,
    challengeName: string
  ): Promise<CognitoAuthResult> {
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: challengeName as ChallengeNameType,
        Session: session,
        ChallengeResponses: {
          USERNAME: this.currentSession.username || '',
          SOFTWARE_TOKEN_MFA_CODE: code,
        },
      });

      const response = await cognitoClient.send(command);
      
      if (response.AuthenticationResult) {

        return {
          isSignedIn: true,
          username: this.currentSession.username,
          accessToken: response.AuthenticationResult.AccessToken,
          idToken: response.AuthenticationResult.IdToken,
          refreshToken: response.AuthenticationResult.RefreshToken,
        };
      }

      return { isSignedIn: false };
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Initiate a custom authentication flow (CUSTOM_AUTH) for passwordless one-time code login.
   * Cognito will trigger the DefineAuthChallenge → CreateAuthChallenge Lambda pipeline,
   * which generates and emails a 6-digit code. The response contains a session token
   * needed for RespondToAuthChallenge.
   */
  async initiateCustomAuth(email: string): Promise<CognitoAuthResult> {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
        },
      });

      const response = await cognitoClient.send(command);
      this.currentSession.username = email;
      return this.handleAuthResponse(response, email);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Respond to a CUSTOM_CHALLENGE with the user-provided one-time code.
   * Used after initiateCustomAuth to submit the 6-digit code for verification.
   */
  async respondToCustomChallenge(
    email: string,
    code: string,
    session: string
  ): Promise<CognitoAuthResult> {
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'CUSTOM_CHALLENGE',
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          ANSWER: code,
        },
      });

      const response = await cognitoClient.send(command);
      return this.handleAuthResponse(response, email);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Refresh the current session using the refresh token
   */
  async refreshSession(refreshToken?: string): Promise<CognitoAuthResult> {
    try {
      const token = refreshToken || this.currentSession.refreshToken;
      if (!token) {
        throw new Error('No refresh token available');
      }

      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: token,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult) {

        return {
          isSignedIn: true,
          username: this.currentSession.username,
          accessToken: response.AuthenticationResult.AccessToken,
          idToken: response.AuthenticationResult.IdToken,
          refreshToken: this.currentSession.refreshToken,
        };
      }

      return { isSignedIn: false };
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(accessToken?: string): Promise<void> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (token) {
        const command = new GlobalSignOutCommand({
          AccessToken: token,
        });
        await cognitoClient.send(command);
      }
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Create a new user account
   */
  async createUser(userData: CreateUserData): Promise<CognitoUser> {
    try {
      const userAttributes = [
        { Name: 'email', Value: userData.email },
        { Name: 'given_name', Value: userData.firstName },
        { Name: 'family_name', Value: userData.lastName },
      ];

      if (userData.phoneNumber) {
        userAttributes.push({ Name: 'phone_number', Value: userData.phoneNumber });
      }

      const command = new SignUpCommand({
        ClientId: CLIENT_ID,
        Username: userData.email,
        Password: userData.password,
        UserAttributes: userAttributes,
      });

      const response = await cognitoClient.send(command);

      return {
        userId: response.UserSub || '',
        username: userData.email,
        email: userData.email,
        emailVerified: response.UserConfirmed || false,
        attributes: {
          given_name: userData.firstName,
          family_name: userData.lastName,
          ...(userData.phoneNumber && { phone_number: userData.phoneNumber }),
        },
      };
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Confirm user email with verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: CLIENT_ID,
        Username: email,
        ConfirmationCode: code,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Resend verification code
   */
  async resendVerificationCode(email: string): Promise<void> {
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: CLIENT_ID,
        Username: email,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }


  /**
   * Update user attributes
   */
  async updateUser(updates: UserUpdates, accessToken?: string): Promise<void> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const attributes: Array<{ Name: string; Value: string }> = [];
      if (updates.firstName) attributes.push({ Name: 'given_name', Value: updates.firstName });
      if (updates.lastName) attributes.push({ Name: 'family_name', Value: updates.lastName });
      if (updates.phoneNumber) attributes.push({ Name: 'phone_number', Value: updates.phoneNumber });
      if (updates.email) attributes.push({ Name: 'email', Value: updates.email });

      const command = new UpdateUserAttributesCommand({
        AccessToken: token,
        UserAttributes: attributes,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Initiate password reset flow
   */
  async initiatePasswordReset(email: string): Promise<void> {
    try {
      const command = new ForgotPasswordCommand({
        ClientId: CLIENT_ID,
        Username: email,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Confirm password reset with code
   */
  async confirmPasswordReset(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: CLIENT_ID,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(
    oldPassword: string,
    newPassword: string,
    accessToken?: string
  ): Promise<void> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const command = new ChangePasswordCommand({
        AccessToken: token,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Set up MFA for the current user
   */
  async setupMFA(accessToken?: string): Promise<MFASetupData> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const command = new AssociateSoftwareTokenCommand({
        AccessToken: token,
      });

      const response = await cognitoClient.send(command);
      const secretCode = response.SecretCode || '';
      const username = this.currentSession.username || 'user';

      // Generate QR code URL for authenticator apps
      const qrCodeUrl = `otpauth://totp/NodeGeeks:${username}?secret=${secretCode}&issuer=NodeGeeks`;

      return {
        secretCode,
        qrCodeUrl,
        session: response.Session,
      };
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Verify MFA code and enable MFA
   */
  async verifyMFA(code: string, accessToken?: string): Promise<void> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Verify the TOTP code
      const verifyCommand = new VerifySoftwareTokenCommand({
        AccessToken: token,
        UserCode: code,
        FriendlyDeviceName: 'Authenticator App',
      });

      await cognitoClient.send(verifyCommand);

      // Enable TOTP as preferred MFA
      const preferenceCommand = new SetUserMFAPreferenceCommand({
        AccessToken: token,
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true,
        },
      });

      await cognitoClient.send(preferenceCommand);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Disable MFA for the current user
   */
  async disableMFA(accessToken?: string): Promise<void> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const command = new SetUserMFAPreferenceCommand({
        AccessToken: token,
        SoftwareTokenMfaSettings: {
          Enabled: false,
          PreferredMfa: false,
        },
      });

      await cognitoClient.send(command);
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }


  /**
   * Get current user information
   */
  async getCurrentUser(accessToken?: string): Promise<CognitoUser | null> {
    try {
      const token = accessToken || this.currentSession.accessToken;
      if (!token) {
        return null;
      }

      const command = new GetUserCommand({
        AccessToken: token,
      });

      const response = await cognitoClient.send(command);
      const attributes: Record<string, string> = {};

      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name] = attr.Value;
        }
      });

      return {
        userId: attributes['sub'] || '',
        username: response.Username || '',
        email: attributes['email'] || '',
        emailVerified: attributes['email_verified'] === 'true',
        attributes,
      };
    } catch {
      // User not authenticated or token expired
      return null;
    }
  }

  /**
   * Get the current access token
   */
  getAccessToken(): string | undefined {
    return this.currentSession.accessToken;
  }

  /**
   * Get the current ID token
   */
  getIdToken(): string | undefined {
    return this.currentSession.idToken;
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return !!this.currentSession.accessToken;
  }

  /**
   * Set session from stored tokens (for session restoration)
   */
  setSession(tokens: {
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
    username?: string;
  }): void {
    this.currentSession = tokens;
  }

  /**
   * Initiate federated sign-in with social provider
   * Note: This requires redirect-based OAuth flow
   */
  getFederatedSignInUrl(provider: 'Google' | 'Facebook' | 'Amazon' | 'Apple'): string {
    const domain = getEnvVar('VITE_AUTH_DOMAIN', '');
    const redirectUri = getEnvVar('VITE_OAUTH_REDIRECT_URI', typeof window !== 'undefined' ? window.location.origin : '');
    const scopes = getEnvVar('VITE_OAUTH_SCOPES', 'openid email profile');

    const params = new URLSearchParams({
      identity_provider: provider,
      redirect_uri: redirectUri,
      response_type: 'code',
      client_id: CLIENT_ID,
      scope: scopes,
    });

    return `https://${domain}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleOAuthCallback(code: string): Promise<CognitoAuthResult> {
    const domain = getEnvVar('VITE_AUTH_DOMAIN', '');
    const redirectUri = getEnvVar('VITE_OAUTH_REDIRECT_URI', typeof window !== 'undefined' ? window.location.origin : '');

    try {
      const response = await fetch(`https://${domain}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: CLIENT_ID,
          code,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange authorization code');
      }

      const tokens = await response.json() as OAuthTokenResponse;

      this.currentSession = {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      };

      // Get user info
      const user = await this.getCurrentUser();

      return {
        isSignedIn: true,
        userId: user?.userId,
        username: user?.username,
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      };
    } catch (error) {
      throw this.handleCognitoError(error);
    }
  }

  /**
   * Handle Cognito errors and convert to standard error format
   */
  private handleCognitoError(error: unknown): Error {
    const cognitoError = error as { name?: string; message?: string };
    const errorName = cognitoError.name;
    const errorMessage = cognitoError.message;

    // Detect rate limit errors from Lambda triggers (wrapped as UserLambdaValidationException)
    if (errorName === 'UserLambdaValidationException' && errorMessage) {
      if (errorMessage.toLowerCase().includes('rate limit')) {
        // Extract retry-after timing from the Lambda error message
        const retryMatch = errorMessage.match(/after (\d+) seconds/);
        const retrySeconds = retryMatch ? parseInt(retryMatch[1], 10) : 0;
        const retryMinutes = Math.ceil(retrySeconds / 60);
        const retryText = retryMinutes > 0 ? ` Please try again in ${retryMinutes} minute(s).` : ' Please try again later.';
        return new Error(`Too many code requests.${retryText}`);
      }
    }

    const errorMap: Record<string, string> = {
      UserNotFoundException: 'User not found',
      NotAuthorizedException: 'Invalid credentials',
      UserNotConfirmedException: 'Email not verified',
      CodeMismatchException: 'Invalid verification code',
      ExpiredCodeException: 'Verification code expired',
      InvalidPasswordException: 'Password does not meet requirements',
      UsernameExistsException: 'User already exists',
      LimitExceededException: 'Too many attempts, please try again later',
      InvalidParameterException: 'Invalid input provided',
      TooManyRequestsException: 'Too many requests, please slow down',
      PasswordResetRequiredException: 'Password reset required',
      UserLambdaValidationException: 'Validation failed',
    };

    if (errorName && errorMap[errorName]) {
      return new Error(errorMap[errorName]);
    }

    if (errorMessage) {
      return new Error(errorMessage);
    }

    return new Error('An unexpected error occurred');
  }
}

// Export singleton instance
export const cognitoService = new CognitoService();
